import colors from 'lib/constants/resourceColors';
import Privileges from 'lib/constants/privileges';
import { SHORT_DATETIME_FORMAT } from 'lib/helpers/dateFormats';

// A VISIT IS A TRIP (#107). This is the screen for one truck-roll: when it was scheduled, the
// timeline the LEAD entered on behalf of everybody on it, how it came out, and -- the reason
// this page exists rather than a modal on the work order -- WHO WAS ON IT.
//
// Henry's three constraints and where each one shows up here:
//   1. Two techs on one trip is normal      -> the Crew grid holds N rows against ONE trip.
//   2. Some techs have no device            -> the timeline fields below are the TRIP's, entered
//                                              once. Crew rows inherit them.
//   3. Both techs see the same visit        -> they do: one row, one number, one resolution.
//
// 🚩 HOURS ARE CALCULATED, NOT TYPED (1786600050). regular_hours / ot_hours on every crew row
// are derived from the EFFECTIVE timeline by a database trigger, on Henry's rules: Mon-Fri
// 8:00am-5:00pm ET is regular; nights, weekends and any holiday flagged "Open for Business =
// No" are OT. Typing over a number latches `hours_overridden` and freezes that row; clearing
// both hours hands it back to the calculator. See migration 1786600050 for the split itself.
//
// 🚩 THERE ARE EXACTLY TWO BUCKETS -- REGULAR AND OT. No double time, by ruling (Henry,
// 2026-08-20). Do not add a third here or anywhere else.
//
// 🚩 TRAVEL IS BILLABLE, by ruling (same date): the split is taken DISPATCH -> END, portal to
// portal, not arrived -> end. That closes what shipped as a flagged default in 1786600050.
//
// 🚩 MINIMUM CHARGE (1786600051): the trip carries a Minimum Charge checkbox, defaulted ON,
// which bills any technician under two hours as two hours. See the field below.
//
// 🚩 THE PER-TECH TIME FIELDS ON A CREW ROW ARE OVERRIDES, AND ARE NORMALLY BLANK. Blank means
// "same as the trip", not "unknown". Only fill one in when that tech genuinely differed (came
// separately, left early). The grid shows the EFFECTIVE times (the trip's, unless overridden)
// so the normal case reads correctly without anyone typing the same three stamps twice.

// Label for one row of /work_order_assignable_users. The view carries the profile name, so the
// dropdown reads like a crew list ("Rolando Diaz (Technician)") instead of a login list; email
// is the fallback for an account with no profile row, and it is also what the Crew grid's
// Technician column shows, so the two stay recognisable as the same person.
const assignableTechLabel = (u) => {
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
    const who = name || u.email;
    return {value: u.id, label: u.classification_name ? `${who} (${u.classification_name})` : who};
};

// Scope of the Add Technician dropdown (Henry, 2026-08-26). The list the #61 view returns is
// every CLASSIFIED person the bound contract prices -- techs, helpers and installers together --
// and on a normal call the dispatcher wants a tech. So default to techs and make the wider list
// one click away rather than the thing he has to read past every time.
//
// The value is a rate_types.code, passed straight through as ?classification=; 'all' is the
// front end's own sentinel for "send no classification param". Codes are lowercase in the
// database ('tech' / 'helper' / 'installer' -- confirmed against rate_types on dev4), NOT the
// uppercase spellings in 1786600000's header comment.
//
// 🚩 'All' means "all ACTIVE ASSIGNABLE users", NOT the full roster. Henry asked for "all
// active users"; what this sends is the contract's assignable set with no classification
// param, so it is already active-only (profiles.end_date, 1786600060) but is still narrowed
// to people the bound contract prices. Widening it to the literal roster would need a
// different endpoint AND would offer names that validateWorkOrderAssignee rejects on save --
// see the Technician field below before changing this.
//
// THREE CHOICES, NOT TWO (Henry, 2026-08-26): Technician (default) / Helper / All. Helper was
// promoted out of the old catch-all because a two-man call is a tech plus a helper, so picking
// the helper was the second most common thing this control does and it should not require
// reading past the installers. Installers have no choice of their own -- they are rare enough
// that 'All' is the right way to reach them.
const CREW_SCOPE_TECH = 'tech';
const CREW_SCOPE_HELPER = 'helper';
const CREW_SCOPE_ALL = 'all';
const CREW_SCOPE_OPTIONS = [
    {value: CREW_SCOPE_TECH,   label: 'Technicians'},
    {value: CREW_SCOPE_HELPER, label: 'Helpers'},
    {value: CREW_SCOPE_ALL,    label: 'All'},
];

const VISIT_OUTCOME_OPTIONS = [
    {value: 'complete',   label: 'Complete'},
    {value: 'incomplete', label: 'Incomplete (return visit needed)'},
];

export const form = [

    // --- Identity ---
    // Trigger-stamped per work order (set_work_order_visit_number): "trip 2 of this call", not
    // a system-wide number. Never user-supplied; the back end lists it readOnly too.
    {key: 'visit_number', label: 'Trip #', static: true, readOnly: true},
    {key: 'work_order_number',
        label: 'Work Order',
        static: true,
        readOnly: true,
        link: {url: '/work_orders/:work_order_number', privileges: Privileges.READ_WORK_ORDERS}},
    {key: 'site_name', label: 'Site', static: true, readOnly: true},
    {key: 'customer_name', label: 'Customer', static: true, readOnly: true},

    // --- The trip ---
    // Setting a date or a dispatch stamp on a credit-held customer/site, or on a call carrying
    // a live #24 hold, is refused by the back end with a 409 (guardWorkOrderScheduling, mode
    // 'visit'). Clearing them is always allowed -- unscheduling a held call is the corrective
    // action. arrived_at / end_at are deliberately NOT gated: once a tech is on site, refusing
    // the timestamp only destroys the audit trail.
    //
    // The three timestamps are entered as a DATE and a TIME separately, both editable, with a
    // Now shortcut on the time. That is the framework's existing pair (see
    // resources/inspectionTimeBlock.js): the `date` field carries the real column, `time_key`
    // points at its partner, and normalizeValues folds the hour/minute back in on submit --
    // the *_time keys never reach the API. ORDER IS LOAD-BEARING: normalizeValues walks fields
    // in order and reads the time out of the payload before the time field's own case deletes
    // it, so each *_time must come AFTER its date.
    //
    // scheduled_date is deliberately NOT split -- it is a DATE column, there is no time of day
    // to enter.
    //
    // 🚩 THESE THREE STAMPS NOW DRIVE BILLING (1786600050). Every crew row's Regular / OT hours
    // is recalculated from this timeline on save -- Mon-Fri 8am-5pm ET is regular, everything
    // else is OT, and a holiday flagged "Open for Business = No" is OT for the whole day. An
    // approximate time of day is no longer harmless here; it changes the invoice.
    {key: 'scheduled_date', label: 'Scheduled Date', type: 'date', todayButton: true},

    {key: 'dispatch_at', label: 'Dispatched', type: 'date', time_key: 'dispatch_at_time', todayButton: true},
    {key: 'dispatch_at_time', label: 'Dispatched Time', type: 'time',
        date_key: 'dispatch_at', nowButton: true},

    {key: 'arrived_at', label: 'Arrived', type: 'date', time_key: 'arrived_at_time', todayButton: true},
    {key: 'arrived_at_time', label: 'Arrived Time', type: 'time',
        date_key: 'arrived_at', nowButton: true},

    {key: 'end_at', label: 'Ended', type: 'date', time_key: 'end_at_time', todayButton: true},
    {key: 'end_at_time', label: 'Ended Time', type: 'time',
        date_key: 'end_at', nowButton: true},

    // --- Minimum charge (Henry, 2026-08-20) ---
    // On by default, and that default is the ruling, not a convenience. It applies PER
    // TECHNICIAN on this trip: any crew member whose calculated Regular + OT comes to less
    // than two hours is billed two hours instead, and the padding is classified by the same
    // calendar rule as the rest of the call -- a short Saturday call pads into OT, a short
    // Tuesday morning call pads into regular. The crew grid prints a `min` marker on the rows
    // where it actually bit.
    //
    // It is PER TRIP on purpose: a second visit is a second truck roll and earns its own
    // minimum. Unticking it re-derives every non-overridden crew row on this trip immediately
    // (the DB cascades on this column, 1786600051) -- there is nothing to re-save by hand.
    //
    // It does NOT apply to a crew row marked non-billable, and it does NOT apply to a row
    // whose hours were typed by hand: a manual number is the final answer.
    {key: 'minimum_charge',
        label: 'Minimum Charge (bills 2 hrs per technician)',
        type: 'boolean',
        defaultValue: true},

    // --- Outcome (#26) ---
    // Leaving this blank means the trip is still open. Marking it Incomplete fires the respawn
    // trigger: a successor trip is created with NO date and NO crew, which is exactly what
    // Henry asked for -- "a new entry appears under Unassigned + Unscheduled". Do not create
    // the return trip by hand as well; you will get two.
    {key: 'outcome', label: 'Outcome', type: 'options', options: VISIT_OUTCOME_OPTIONS},
    {key: 'resolution',
        label: 'Resolution',
        type: 'longtext',
        placeholder: 'What was done on THIS trip'},

    // --- Crew (#107, #59/#60/#61, #109) ---
    // Fetch mode: the grid GETs dataUrl on mount and re-fetches after each create/edit/delete.
    // It reads work_order_visit_techs_extended, so every row already carries effective_* times
    // and the user's email -- no join in the browser.
    {key: 'techs',
        type: 'references',
        heading: 'Crew',
        createLabel: 'Add Technician',
        createPrivileges: [Privileges.CREATE_WORK_ORDER_VISIT_TECHS],
        readPrivileges: [Privileges.READ_WORK_ORDER_VISIT_TECHS],
        editPrivileges: [Privileges.UPDATE_WORK_ORDER_VISIT_TECHS],
        deletePrivileges: [Privileges.DELETE_WORK_ORDER_VISIT_TECHS],
        deleteLabel: 'Remove From Trip',
        dataUrl: '/work_order_visit_techs?work_order_visit=:id',
        createUrl: () => '/work_order_visit_techs',
        editUrl: '/work_order_visit_techs/:id',
        deleteUrl: '/work_order_visit_techs/:id',
        injectedValues: (values) => ({work_order_visit_id: values.id}),
        // Hands the trip's bound contract to the Add Technician modal so the Technician
        // dropdown can filter by it (see the field below). ONLY contract_id is passed: the
        // create form reads `values[field.key]` for its initial values, so anything else in
        // here would silently pre-fill a crew field. The submitted body is built from the
        // form's own fields, so contract_id is not posted.
        modalValuesTransform: (values) => ({contract_id: values.contract_id}),
        formFields: [
            // SCOPE FIRST, THEN THE PERSON (Henry, 2026-08-26). This narrows the dropdown below
            // by labor classification and nothing else; it defaults to techs.
            //
            // `transient` keeps the control out of the POST body -- work_order_visit_techs has
            // no such column and the CRUD layer would reject the key. `inputRef` publishes its
            // live value into the form's inputRefs, which is the SECOND argument optionsURL
            // receives. Picking a scope re-renders the form -> new URL -> refetch, which is the
            // only thing RemoteSelect reacts to (components/Form/RemoteSelect refetches on a
            // url change and on nothing else).
            {key: 'crew_scope',
                label: 'Show',
                type: 'options',
                transient: true,
                inputRef: 'crew_scope',
                defaultValue: CREW_SCOPE_TECH,
                options: CREW_SCOPE_OPTIONS},

            // A DROPDOWN, NOT A SEARCH BOX (Henry, 2026-08-26). A dispatcher assigning a trip
            // does not know the roster by email prefix, and the old type-ahead showed nothing
            // at all until he typed -- so the only way to learn who was assignable was to
            // guess. The crew is a few dozen people; print the list.
            //
            // ✅ THE #61 NARROWING GAP IS NOW CLOSED. The old note here said the picker could
            // not be filtered to the work order's contract because `suggestUrl` is a static
            // string in the Form layer. `optionsURL` is not: the 'options' item passes it
            // through as a FUNCTION of the form's values (components/Form/Options), and
            // modalValuesTransform above puts contract_id in those values. So the list is now
            // the assignable set FOR THIS CONTRACT, and picking someone the contract does not
            // price is no longer possible from the UI.
            //
            // 🚩 STILL NOT THE CONTROL, and this has not changed: the write path is enforced
            // in helpers/hooks/validateWorkOrderAssignee.js, which re-checks classification and
            // the contract rate on every create AND on every tech swap. A filtered dropdown is
            // one curl away from being bypassed. Do not move the rule up here.
            //
            // WHAT THE LIST EXCLUDES (view work_order_assignable_users):
            //   * no classification, or a rate type flagged inactive        (1786600020, #61)
            //   * no rate for that classification on THIS contract          (1786600020, #61)
            //   * left the company -- profiles.end_date in the past         (1786600060)
            // and what it does NOT exclude: someone already on this trip (the unique index
            // 409s that), and someone on vacation that day (the trip often has no date yet --
            // see 1786600060's header).
            //
            // Empty string when contract_id is missing: no contract, no defensible list. It
            // cannot happen from this screen (work_orders.contract_id is NOT NULL and the
            // visit view reads it through), and the fallback is a blank dropdown rather than
            // the unfiltered roster on purpose -- offering everyone would resurrect the gap
            // this field just closed.
            {key: 'user_id',
                label: 'Technician',
                required: true,
                type: 'options',
                // Sorted by the API, not here: work_order_assignable_users sorts
                // classification, then last/first name, then email (est-back-end/resources/
                // work_order_assignable_users.js). RemoteSelect renders rows in the order they
                // arrive and has no sort hook, so ordering belongs in the ORDER BY.
                //
                // The scope narrows by CLASSIFICATION ONLY. Everything the view already excludes
                // stays excluded in both modes -- including people who have left the company
                // (profiles.end_date, 1786600060), so "All" means all ASSIGNABLE users, not the
                // roster. No "show terminated" choice was added: a trip is being crewed now,
                // and the users screen defaults to Active for the same reason. Note this is a
                // LIST rule, not a write rule -- validateWorkOrderAssignee re-checks
                // classification and the contract rate but deliberately does NOT check
                // employment (1786600060), so a past call can still be corrected by API.
                optionsURL: (values, inputRefs) => {
                    const contractId = values && values.contract_id;
                    if(!contractId) {
                        return '';
                    }
                    // undefined until the dispatcher touches the control (inputRefs starts
                    // empty and is reset with the modal), so the default lives here too.
                    const scope = (inputRefs && inputRefs.crew_scope) || CREW_SCOPE_TECH;
                    const base = `/work_order_assignable_users?contract=${contractId}`;
                    return scope === CREW_SCOPE_ALL ? base : `${base}&classification=${scope}`;
                },
                optionsMapper: assignableTechLabel},
            // At most one lead per trip -- enforced by a partial unique index, so a second one
            // comes back as a 409 from the database rather than being silently accepted.
            {key: 'is_lead', label: 'Lead (enters the times for the crew)', type: 'boolean'},
            // TICKED BY DEFAULT (Henry, 2026-08-26). Nothing anywhere derives this: it is not
            // on the customer, not on the contract, not on the tech's record, and no hook or
            // trigger sets it. Its ONLY source is the column default (work_order_visit_techs
            // .billable NOT NULL DEFAULT true), which the API never even sees -- an untouched
            // checkbox submits null and normalizeValues drops the key, so the row has always
            // landed billable. The box just RENDERED unticked, telling the dispatcher the
            // opposite of what was about to be saved. This aligns the form with the column.
            //
            // Unticking it is still the deliberate act it always was (warranty, callback,
            // goodwill) and is what suppresses the #26 minimum charge for that person.
            // Nothing about the money changes here -- this is the checkbox's initial state.
            {key: 'billable', label: 'Billable', type: 'boolean', defaultValue: true},
        ],
        editFields: [
            {key: 'is_lead', label: 'Lead (enters the times for the crew)', type: 'boolean'},

            // --- Hours: DERIVED, not typed (1786600050) -------------------------------------
            // The database computes these from the EFFECTIVE trip timeline every time the times
            // move: Mon-Fri 8:00am-5:00pm ET is regular, everything else -- nights, weekends,
            // and any holiday flagged "Open for Business = No" on the Holidays screen -- is OT.
            // The normal path is to leave both alone and just enter the times on the trip.
            //
            // They stay EDITABLE on purpose. Typing a different number latches
            // `hours_overridden` in the trigger, and from then on the row is left alone no
            // matter how the timeline moves. That is the escape hatch for the call where the
            // stamps are wrong and nobody can reconstruct them.
            //
            // TO UNDO A MANUAL CORRECTION: clear BOTH hour fields (or untick the checkbox
            // below) and save -- the row recalculates on the spot.
            {key: 'regular_hours', label: 'Regular Hours (calculated)', type: 'number'},
            {key: 'ot_hours', label: 'OT Hours (calculated)', type: 'number'},
            // Set by the trigger, not normally by hand; unticking it is the documented way to
            // hand a row back to the calculator.
            {key: 'hours_overridden',
                label: 'Hours entered manually (untick to recalculate)', type: 'boolean'},

            {key: 'billable', label: 'Billable', type: 'boolean'},
            // Overrides. Leave blank unless this person genuinely differed from the trip.
            //
            // Dispatched is split into date + time with the same Today/Now pair as the trip
            // timeline above, and for the same reason: this modal is where a lead corrects one
            // tech's stamp, so the time of day is the whole point of the field. The mechanics
            // are identical -- `time_key`/`date_key` pair the two, normalizeValues folds the
            // hour/minute into dispatch_at on submit and drops dispatch_at_time, so the API
            // still sees only the real column. ORDER IS LOAD-BEARING: dispatch_at_time must
            // stay immediately after dispatch_at.
            //
            // Blank is the normal state here (blank = "same as the trip"), and it stays that
            // way: the pair renders empty, and Now is the only control that writes both halves.
            // Today alone sets midnight -- same as on the trip fields.
            {key: 'dispatch_at', label: 'Dispatched (override)', type: 'date',
                time_key: 'dispatch_at_time', todayButton: true},
            {key: 'dispatch_at_time', label: 'Dispatched Time (override)', type: 'time',
                date_key: 'dispatch_at', nowButton: true},
            //
            // ⚠️ Arrived and Ended carry a TIME half too, and they did not before 1786600050.
            // A date-only override lands at MIDNIGHT, which was merely cosmetic while hours
            // were hand-typed and is a real defect now that they are derived from these
            // stamps: a midnight Ended would book the whole night as OT. Same ORDER rule --
            // each *_time stays immediately after its date.
            {key: 'arrived_at', label: 'Arrived (override)', type: 'date',
                time_key: 'arrived_at_time', todayButton: true},
            {key: 'arrived_at_time', label: 'Arrived Time (override)', type: 'time',
                date_key: 'arrived_at', nowButton: true},

            {key: 'end_at', label: 'Ended (override)', type: 'date',
                time_key: 'end_at_time', todayButton: true},
            {key: 'end_at_time', label: 'Ended Time (override)', type: 'time',
                date_key: 'end_at', nowButton: true},
        ],
        tableProps: {
            columns: [
                {heading: 'Technician', accessor: 'user_email'},
                // valueFunc on a text column is called (record, value) -- see
                // components/Table renderFunc. Taking the value as the FIRST argument gets you
                // the whole row, which is always truthy, so every row reads 'Lead'.
                {heading: 'Lead', accessor: 'is_lead',
                    valueFunc: (row, v) => v ? 'Lead' : ''},
                // EFFECTIVE times, never the raw override columns -- the raw ones are NULL in
                // the normal case and would render "no arrival" for everyone but the exception.
                {heading: 'Dispatched', accessor: 'effective_dispatch_at', type: 'date',
                    dateFormat: SHORT_DATETIME_FORMAT},
                {heading: 'Arrived', accessor: 'effective_arrived_at', type: 'date',
                    dateFormat: SHORT_DATETIME_FORMAT},
                {heading: 'Ended', accessor: 'effective_end_at', type: 'date',
                    dateFormat: SHORT_DATETIME_FORMAT},
                {heading: '', accessor: 'has_time_override',
                    cellWidth: '90px',
                    valueFunc: (row, v) => v ? 'overridden' : ''},
                {heading: 'Reg Hrs', accessor: 'regular_hours', cellWidth: '80px'},
                {heading: 'OT Hrs', accessor: 'ot_hours', cellWidth: '80px'},
                // Blank is the normal case (hours came from the timeline). "manual" means
                // somebody typed over the calculated split and the trigger no longer maintains
                // this row -- the same exception-marker idea as the override column above.
                {heading: '', accessor: 'hours_overridden',
                    cellWidth: '80px',
                    valueFunc: (row, v) => v ? 'manual' : ''},
                // Blank is the normal case. `min` means this tech's calculated total came to
                // less than two hours and the trip's Minimum Charge box topped it up -- so the
                // Reg/OT figures on this row are the MINIMUM, not the measured time. Same
                // exception-marker idea as `manual` and `overridden`.
                {heading: '', accessor: 'minimum_charge_applied',
                    cellWidth: '70px',
                    valueFunc: (row, v) => v ? 'min' : ''},
                // 🔒 #109 -- FIELD TECHS SEE NO PRICING. These three columns are gated on the
                // same privileges the API uses to STRIP the fields server-side, so a tech-role
                // session gets neither the column nor the value. The gate is the server's; this
                // only keeps the UI from rendering an empty money column at someone who is
                // never allowed to see it.
                {heading: 'Class', accessor: 'rate_type_code', cellWidth: '90px',
                    privileges: [Privileges.READ_WORK_ORDER_VISIT_TECHS_RATE_TYPE_CODE]},
                {heading: 'Rate', accessor: 'regular_rate', type: 'currency',
                    privileges: [Privileges.READ_WORK_ORDER_VISIT_TECHS_REGULAR_RATE]},
                {heading: 'OT Rate', accessor: 'ot_rate', type: 'currency',
                    privileges: [Privileges.READ_WORK_ORDER_VISIT_TECHS_OT_RATE]},
                {heading: 'Billable', accessor: 'billable',
                    valueFunc: (row, v) => v ? 'Yes' : 'No'},
            ]
        }
    },
];

export default {
    defaults: {
        name: 'Work Order Visit',
        color: colors.workOrders,
        formFields: form,
        // No 'search' and no 'create': a trip is always reached from, and created on, its work
        // order. A standalone "all visits" list is the dispatch board (#67), which Henry
        // explicitly deferred -- building half of it here by accident would pre-empt that
        // design.
        actions: ['view'],
    },

    view: {
        title: (v) => v.work_order_number
            ? `${v.work_order_number} · Trip ${v.visit_number}`
            : 'Visit',
        // Deleting a trip is Administrator-only (1786600040): it destroys the #26 respawn chain
        // and the timeline the report and the invoice are derived from. Send the user back to
        // the call either way.
        deleteRedirect: (v) => v && v.work_order_number
            ? `/work_orders/${v.work_order_number}`
            : '/work_orders',
        notFoundRedirect: () => '/work_orders',
    },
};
